import React, { useCallback, useEffect, useMemo, useState } from "react";
import { StackScreenProps } from "@react-navigation/stack";
import ScreenCard from "../ScreenCard";
import { useI18n } from "../../hooks/i18n-hooks";
import { useSnackbar } from "../../hooks/snackbar";
import { useMutation } from "@apollo/client/react";
import { CHANGE_MISSION_SIGNER, GET_MISSION_WITH_TERMINATION } from "../../graphql/core";
import { CHANGE_AMENDMENT_SIGNER } from "../../graphql/amendment";
import { RootStackParamList } from "../../navigations/RootNavigation";
import VStack from "../../ui_atoms/VStack";
import ListItem from "../../ui_atoms/ListItem";
import { useTheme } from "../../hooks/use-theme";
import { Bubble } from "../../components/Bubble";
import DocuIllSvg from "../../assets/images/docu_ill.svg";
import { useStepsHeader } from "../../hooks/use-steps-header";
import { useBubbleStepOrigin } from "../../hooks/use-bubble-step-origin";
import { TitleMediumText } from "../../components/FormattedText";
import { StyleSheet } from "react-native";
import { useEffectiveClientUsers } from "../../hooks/use-effective-client-users";

type MissionSelectSignerScreenProps = StackScreenProps<
    RootStackParamList,
    "MissionSelectSignerScreen"
>;

const CURRENT_STEP_SIZE = 44;
const CurrentStep = <DocuIllSvg width="100%" height="100%" />;

const MissionSelectSignerScreen: React.FC<MissionSelectSignerScreenProps> = ({
    route,
    navigation,
}) => {
    const { missionId, showCurrentProfile = false, amendmentId } = route.params;
    const i18n = useI18n();
    const { colors } = useTheme();
    const { showSnackbar } = useSnackbar();
    const [working, setWorking] = useState<boolean>(false);
    const [changeMissionSigner] = useMutation(CHANGE_MISSION_SIGNER);
    const [changeAmendmentSigner] = useMutation(CHANGE_AMENDMENT_SIGNER);

    const { headerComponent, screenListeners } = useStepsHeader<keyof RootStackParamList>({
        getCurrentIndex: () => 0,
        getTotalSteps: () => 1,
        stepsConfig: {
            seenColor: colors.primary,
            currentColor: colors.accentLighter,
            currentStep: {
                component: CurrentStep,
                size: CURRENT_STEP_SIZE,
            },
        },
    });

    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        screenListeners({ navigation, route });
    }, [navigation, route, screenListeners]);

    const { clientUsers, selectedClientUserId } = useEffectiveClientUsers({ showCurrentProfile });

    const sendContract = useCallback(() => {
        const _func = async () => {
            if (!selectedClientUserId) {
                return;
            }
            setWorking(true);
            try {
                await (amendmentId ? changeAmendmentSigner : changeMissionSigner)({
                    variables: {
                        amendmentId: amendmentId || "",
                        missionId: missionId,
                        clientUserId: selectedClientUserId,
                    },
                    errorPolicy: "all",
                    refetchQueries: [
                        {
                            query: GET_MISSION_WITH_TERMINATION,
                            variables: { id: missionId },
                        },
                    ],
                });
                void showSnackbar({
                    text: i18n.t("screens.mission_select_signer.contract_sent"),
                    mode: "success",
                });
                navigation.popTo("Home", {
                    screen: "MissionsScreen",
                    params: {
                        screen: "MissionDetails",
                        params: {
                            missionId,
                        },
                    },
                });
            } catch (_error) {
                void showSnackbar({
                    text: i18n.t("screens.mission_select_signer.contract_send_error"),
                    mode: "error",
                });
            } finally {
                setWorking(false);
            }
        };
        void _func();
    }, [
        amendmentId,
        changeAmendmentSigner,
        changeMissionSigner,
        missionId,
        i18n,
        navigation,
        selectedClientUserId,
        showSnackbar,
    ]);

    const signLater = useCallback(() => {
        navigation.popTo("Home", {
            screen: "MissionsScreen",
            params: {
                screen: "MissionDetails",
                params: {
                    missionId,
                },
            },
        });
    }, [missionId, navigation]);

    const origin = useBubbleStepOrigin("__DEFAULT__");
    const styles = useStyles();

    return (
        <>
            {headerComponent}
            <ScreenCard
                isContentContainerFlex
                buttons={[
                    {
                        mode: "secondary",
                        title: i18n.t("screens.mission_select_signer.send_contract"),
                        isValid: !working && !!selectedClientUserId,
                        onPress: sendContract,
                        validating: working,
                    },
                    {
                        mode: "outlined",
                        title: i18n.t("screens.mission_select_signer.postpone_signing"),
                        onPress: signLater,
                        isValid: !working,
                    },
                ]}
            >
                <Bubble
                    title={i18n.t("screens.mission_select_signer.description")}
                    bubbleStyle={{ paddingBottom: 0 }}
                    isAnimated={false}
                    origin={origin}
                    isInitial
                >
                    {clientUsers.length > 0 && (
                        <VStack h="-2/3" mode="margin">
                            {clientUsers.map((clientUser) => (
                                <ListItem
                                    key={clientUser.id}
                                    {...clientUser}
                                    useBigAvatarOrIcon
                                    checked={selectedClientUserId === clientUser.id}
                                />
                            ))}
                        </VStack>
                    )}
                </Bubble>
                <VStack h="2/3" style={styles.bottomWrapper}>
                    <TitleMediumText style={styles.bottomText}>
                        {i18n.t("screens.mission_select_signer.no_other_signers")}
                    </TitleMediumText>
                </VStack>
            </ScreenCard>
        </>
    );
};

const useStyles = () => {
    const { colors } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                bottomWrapper: {
                    flex: 1,
                    alignItems: "center",
                    justifyContent: "center",
                },
                bottomText: {
                    textAlign: "center",
                    color: colors.fieldLabel,
                },
            }),
        [colors.fieldLabel],
    );
};

export default MissionSelectSignerScreen;
